import React, { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { X } from "lucide-react";

/**
 * HomePagePopup — shows the active popup after `display_delay_seconds`,
 * then auto-closes after `auto_close_seconds`. Users can also close manually.
 */
export default function HomePagePopup() {
  const [popup, setPopup] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let showTimer, closeTimer;
    (async () => {
      try {
        const { data } = await api.get(`/popups/active?_=${Date.now()}`);
        if (!data || !data.image_url) return;
        // Session-once: don't show if user already dismissed in this browser session
        if (sessionStorage.getItem(`useko_popup_${data.id}`) === "dismissed") return;
        setPopup(data);
        const delay = Math.max(0, (data.display_delay_seconds ?? 5) * 1000);
        const auto = Math.max(0, (data.auto_close_seconds ?? 5) * 1000);
        showTimer = setTimeout(() => {
          setVisible(true);
          if (auto > 0) closeTimer = setTimeout(() => setVisible(false), auto);
        }, delay);
      } catch (e) { /* silent */ }
    })();
    return () => { clearTimeout(showTimer); clearTimeout(closeTimer); };
  }, []);

  const close = () => {
    if (popup) sessionStorage.setItem(`useko_popup_${popup.id}`, "dismissed");
    setVisible(false);
  };

  if (!popup || !visible) return null;

  const Wrapper = popup.link_url ? "a" : "div";
  const linkProps = popup.link_url
    ? { href: popup.link_url, target: "_blank", rel: "noreferrer" }
    : {};

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-fadeIn"
      onClick={close}
      data-testid="home-popup-overlay"
    >
      <div
        className="relative max-w-3xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-[#D4A94F] hover:bg-[#F5DE8C] text-[#051436] flex items-center justify-center shadow-xl z-10 transition-colors"
          data-testid="home-popup-close"
          aria-label="Close"
        >
          <X className="w-5 h-5"/>
        </button>
        <Wrapper {...linkProps} className="block rounded-2xl overflow-hidden shadow-2xl">
          <img
            src={fileUrl(popup.image_url)}
            alt={popup.title || "Duyuru"}
            className="w-full h-auto object-contain bg-[#051436]"
            data-testid="home-popup-image"
          />
        </Wrapper>
      </div>
    </div>
  );
}
